import React, { useState } from "react";
import { customerRepo, paymentLinkRepo, transactionService } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { onlinePaymentService } from "../../services/onlinePayment";
import { addDaysIso, effectiveLinkStatus, newLinkCode, payUrl } from "../../config/online-payment";
import { Button, DataTable, Input, Modal, PageHead, Select, StatusBadge, Tabs, baht, formatDate, useAsync, useTableState } from "../../components/ui";

const EMPTY = { customerId: "", title: "", amount: "" };

export default function Payments() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const [tab, setTab] = useState("links");
  const tableState = useTableState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const { data, loading, reload } = useAsync(
    () => paymentLinkRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, sortBy: "createdAt", sortDir: "desc" }),
    [orgId, tableState.page, tableState.perPage, tableState.search]
  );
  const { data: transactions, loading: loadingTxn } = useAsync(
    () => transactionService.list({ page: 1, perPage: 20, orderType: undefined }),
    [tab],
    { rows: [], total: 0 }
  );

  const create = async () => {
    const customer = customers?.find((c) => c.id === form.customerId);
    if (!customer || !form.title || !form.amount) return toast(tx("กรอกข้อมูลให้ครบ", "Please fill all fields"), "error");

    const settings = await onlinePaymentService.getSettings(orgId);
    await paymentLinkRepo.create(orgId, {
      code: newLinkCode(),
      title: form.title,
      customerId: customer.id,
      customerName: customer.name,
      amount: Number(form.amount),
      status: "active",
      expireAt: addDaysIso(settings.linkExpireDays),
    });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("สร้าง Payment Link เรียบร้อย", "Payment link created"), "success");
  };

  const copyLink = (row) => {
    // ลิงก์จริงของเว็บนี้ (หน้า /pay/:code เปิดได้โดยไม่ต้องล็อกอิน)
    navigator.clipboard?.writeText(payUrl(row.code));
    toast(tx("คัดลอกลิงก์แล้ว", "Link copied"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("Payment Link & ธุรกรรม", "Payment links & transactions")}
        subtitle={tx("สร้างลิงก์รับชำระเงินส่งให้ลูกค้า หรือดูประวัติธุรกรรมจริงจากระบบชำระเงิน", "Create payment links or review real gateway transactions")}
        actions={tab === "links" ? <Button variant="primary" onClick={() => setOpen(true)}>+ {tx("สร้าง Payment Link", "New link")}</Button> : null}
      />

      <Tabs tabs={[{ key: "links", label: tx("Payment Link", "Payment links") }, { key: "transactions", label: tx("ธุรกรรมจริง (API)", "Live transactions (API)") }]} value={tab} onChange={setTab} />

      {tab === "links" ? (
        <DataTable
          loading={loading}
          rows={data?.rows || []}
          total={data?.total || 0}
          page={tableState.page}
          perPage={tableState.perPage}
          onPage={tableState.setPage}
          onPerPage={tableState.setPerPage}
          emptyText={tx("ยังไม่มี Payment Link", "No payment links")}
          columns={[
            { key: "code", label: tx("รหัส", "Code") },
            { key: "title", label: tx("รายการ", "Title") },
            { key: "customerName", label: tx("ลูกค้า", "Customer") },
            { key: "amount", label: tx("จำนวนเงิน", "Amount"), align: "right", render: (row) => baht(row.amount) },
            { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={effectiveLinkStatus(row)} /> },
            { key: "expireAt", label: tx("หมดอายุ", "Expires"), render: (row) => formatDate(row.expireAt) },
            { key: "action", label: "", render: (row) => <Button size="sm" onClick={() => copyLink(row)}>{tx("คัดลอกลิงก์", "Copy link")}</Button> },
          ]}
        />
      ) : (
        <DataTable
          loading={loadingTxn}
          rows={transactions?.rows || []}
          emptyText={tx("ยังไม่มีธุรกรรม หรือยังไม่ได้เข้าสู่ระบบ", "No transactions yet, or not logged in")}
          columns={[
            { key: "code", label: tx("เลขที่ออเดอร์", "Order no.") },
            { key: "orderType", label: tx("ประเภท", "Type") },
            { key: "amount", label: tx("จำนวนเงิน", "Amount"), align: "right", render: (row) => baht(row.amount) },
            { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
            { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          ]}
        />
      )}

      <Modal
        open={open}
        title={tx("สร้าง Payment Link", "New payment link")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={create}>{tx("สร้างลิงก์", "Create")}</Button>
          </>
        }
      >
        <Select label={tx("ลูกค้า", "Customer")} placeholder={tx("เลือกลูกค้า", "Select customer")} options={(customers || []).map((c) => ({ value: c.id, label: c.name }))} value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} />
        <Input label={tx("รายการ", "Title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input label={tx("จำนวนเงิน", "Amount")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
      </Modal>
    </>
  );
}
